import React from 'react';
import { motion } from 'framer-motion';
export const CHECKOUT_URL = 'https://feng-shui-negocios.impultienda.ar/checkout/feng-shui-negocios';
export const PRODUCT_NAME = 'FENG SHUI NEGOCIOS™';
export const PRICE = '$24.990 ARS';
export const PRICE_SHORT = '$24.990';
export const PRICE_FULL = '$45.990';
export const SUPPORT_EMAIL = 'visiondigitalsoporte@gmail.com';
export const INSTAGRAM_HANDLE = '@vision.digital02';
export const INSTAGRAM_LINK = 'https://instagram.com/vision.digital02';
export const EASE = [0.23, 1, 0.32, 1] as const;
type SectionProps = {
  children: React.ReactNode;
  className?: string;
  id?: string;
  glow?: boolean;
};
export function Section({
  children,
  className = '',
  id,
  glow = false
}: SectionProps) {
  return <motion.section id={id} initial={{
    opacity: 0,
    y: 22
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-100px'
  }} transition={{
    duration: 0.3,
    ease: EASE
  }} className={`relative mx-auto w-full max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8 ${className}`} data-id="element-330" data-mp="pcl2c7fqe3jq">
      {glow && <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" data-id="element-331" data-mp="34l66yhnh0ndl">
          <div className="animate-chi-1 absolute -top-16 left-[10%] h-72 w-72 rounded-full bg-brand-jadeMist blur-[100px]" data-id="element-332" data-mp="30j2vvu4prksu" />
          <div className="animate-chi-2 absolute -bottom-12 right-[8%] h-64 w-64 rounded-full bg-brand-waterMist blur-[110px]" data-id="element-333" data-mp="2vj6pjo8jnha4" />
        </div>}
      {children}
    </motion.section>;
}
type EyebrowProps = {
  children: React.ReactNode;
  className?: string;
};
export function Eyebrow({
  children,
  className = ''
}: EyebrowProps) {
  return <p className={`font-title text-[11px] uppercase tracking-[0.3em] text-brand-cinnabar md:text-xs ${className}`} data-id="element-334" data-mp="2gt96fmph1hrs">
      {children}
    </p>;
}
type SectionHeadingProps = {
  eyebrow?: string;
  children: React.ReactNode;
  lead?: React.ReactNode;
  align?: 'center' | 'left';
  className?: string;
};

/** Encabezado de sección: filete doble + título en tinta jade, para que los títulos resalten. */
export function SectionHeading({
  eyebrow,
  children,
  lead,
  align = 'center',
  className = ''
}: SectionHeadingProps) {
  const isCenter = align === 'center';
  return <div className={`${isCenter ? 'mx-auto text-center' : 'text-left'} max-w-3xl ${className}`} data-id="element-335" data-mp="1jb2htpjetdip">
      {eyebrow && <Eyebrow data-id="element-336" data-mp="1o9dr9z5slm0e">{eyebrow}</Eyebrow>}
      <h2 className="mt-3 font-title text-3xl leading-tight text-brand-jadeDeep md:text-5xl" data-id="element-337" data-mp="2byg8j5wilcud">{children}</h2>
      <div className={`double-rule mt-5 w-24 ${isCenter ? 'mx-auto' : ''}`} aria-hidden="true" data-id="element-338" data-mp="2gx9n71njf9w4" />
      {lead && <p className="mt-5 font-serif text-base leading-relaxed text-brand-inkLight md:text-lg" data-id="element-339" data-mp="228wlrp5okyu8">{lead}</p>}
    </div>;
}
type CTAButtonProps = {
  children: React.ReactNode;
  href?: string;
  className?: string;
  fullWidth?: boolean;
  variant?: 'buy' | 'outline';
};

/** Botón de compra: cinabrio con doble filete dorado, el elemento más contrastado de la página. */
export function CTAButton({
  children,
  href = '#comprar',
  className = '',
  fullWidth = false,
  variant = 'buy'
}: CTAButtonProps) {
  const colors = variant === 'buy' ? 'bg-brand-cinnabar text-white ring-2 ring-brand-gold ring-offset-2 ring-offset-brand-paper hover:bg-brand-cinnabarDark' : 'bg-transparent text-brand-jadeDeep ring-1 ring-brand-jade/60 hover:bg-brand-jadeMist';
  return <motion.a href={href} whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} transition={{
    duration: 0.14,
    ease: EASE
  }} className={`inline-flex items-center justify-center gap-2 rounded-sm px-8 py-4 font-title text-base uppercase tracking-[0.12em] shadow-[0_12px_26px_rgba(126,22,20,0.28)] transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-cinnabar/50 ${colors} ${fullWidth ? 'w-full' : ''} ${className}`} data-id="element-340" data-mp="t0jwzhhkofzf">
      {children}
    </motion.a>;
}
const elementDot: Record<string, string> = {
  Madera: 'bg-brand-jade',
  Fuego: 'bg-brand-cinnabar',
  Tierra: 'bg-brand-wood',
  Metal: 'bg-brand-silver',
  Agua: 'bg-brand-water'
};
type ChipProps = {
  children: string;
  className?: string;
};
export function Chip({
  children,
  className = ''
}: ChipProps) {
  const dot = elementDot[children];
  return <span className={`inline-flex items-center gap-1.5 rounded-sm border border-brand-ink/12 bg-brand-rice px-2.5 py-1 text-[11px] font-medium text-brand-inkLight ${className}`} data-id="element-341" data-mp="2pxplj4m5170g">
      <span className={`h-1.5 w-1.5 rounded-full ${dot ?? 'bg-brand-gold'}`} aria-hidden="true" data-id="element-342" data-mp="2zwf5r41ib3jv" />
      {children}
    </span>;
}